/**
 * School Match - Parent Facing School Search
 * Parents can search and find best schools for their children
 */
import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// School Card Component
const SchoolCard = ({ school, onInterest }) => {
  const fillPercentage = school.class_info?.total_seats 
    ? ((school.class_info.total_seats - school.class_info.available_seats) / school.class_info.total_seats) * 100 
    : 0;

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 border">
      {/* School Header */}
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-4 text-white">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="font-bold text-lg">{school.school_name}</h3>
            <p className="text-sm opacity-80">
              {school.address?.city || school.address?.district}, {school.address?.state}
            </p>
          </div>
          {school.match_score && (
            <div className="bg-white text-orange-500 px-3 py-1 rounded-full text-sm font-bold">
              {school.match_score}% Match
            </div>
          )}
        </div>
      </div>

      {/* School Info */}
      <div className="p-4 space-y-4">
        {/* Board & Distance */}
        <div className="flex gap-2 flex-wrap">
          {school.board && (
            <span className="px-2 py-1 bg-blue-100 text-blue-600 rounded-full text-xs">
              📚 {school.board}
            </span>
          )}
          {school.distance_km && (
            <span className="px-2 py-1 bg-green-100 text-green-600 rounded-full text-xs">
              📍 {school.distance_km} km away
            </span>
          )}
          {school.discount_available && (
            <span className="px-2 py-1 bg-yellow-100 text-yellow-600 rounded-full text-xs">
              🎁 Discount Available
            </span>
          )}
        </div>

        {/* Class & Fee Info */}
        <div className="bg-gray-50 rounded-xl p-3">
          <div className="flex justify-between items-center mb-2">
            <span className="text-gray-600">{school.class_info?.class_name}</span>
            <span className="font-bold text-lg">₹{school.class_info?.annual_fee?.toLocaleString()}/yr</span>
          </div>
          
          {/* Seat Availability Bar */}
          <div className="space-y-1">
            <div className="flex justify-between text-xs text-gray-500">
              <span>🪑 Seats</span>
              <span>{school.class_info?.available_seats} / {school.class_info?.total_seats} available</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className={`h-2 rounded-full ${
                  fillPercentage >= 90 ? 'bg-red-500' :
                  fillPercentage >= 70 ? 'bg-orange-500' :
                  'bg-green-500'
                }`}
                style={{ width: `${fillPercentage}%` }}
              />
            </div>
          </div>
        </div>

        {/* Live Indicator */}
        {school.class_info?.available_seats <= 10 && school.class_info?.available_seats > 0 && (
          <div className="flex items-center gap-2 text-red-500 text-sm animate-pulse">
            <span className="w-2 h-2 bg-red-500 rounded-full"></span>
            Only {school.class_info.available_seats} seats left! Book now!
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2">
          <button
            onClick={() => onInterest(school)}
            className="flex-1 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
          >
            💚 I'm Interested
          </button>
          {school.contact_whatsapp && (
            <a
              href={`https://wa.me/91${school.contact_whatsapp}?text=Hi, I'm interested in admission for ${school.class_info?.class_name}`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-3 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-all"
            >
              💬
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

// Interest Modal
const InterestModal = ({ isOpen, school, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    parent_name: '',
    parent_phone: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const result = await onSubmit({
      ...formData,
      school_id: school.school_id,
      class_needed: school.class_info?.class_name || 'Class 1'
    });
    setSubmitting(false);
    if (result.success) {
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 3000);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden">
        {success ? (
          <div className="p-8 text-center">
            <span className="text-6xl">🎉</span>
            <h3 className="text-xl font-bold mt-4">Interest Registered!</h3>
            <p className="text-gray-500 mt-2">
              {school.school_name} will contact you soon on WhatsApp
            </p>
          </div>
        ) : (
          <>
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-4 text-white">
              <h2 className="font-bold text-lg">📝 Express Interest</h2>
              <p className="text-sm opacity-80">{school.school_name}</p>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">आपका नाम *</label>
                <input
                  type="text"
                  required
                  value={formData.parent_name}
                  onChange={(e) => setFormData({...formData, parent_name: e.target.value})}
                  className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Enter your name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp Number *</label>
                <input
                  type="tel"
                  required
                  value={formData.parent_phone}
                  onChange={(e) => setFormData({...formData, parent_phone: e.target.value})}
                  className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="98765 43210"
                  pattern="[0-9]{10}"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message (Optional)</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Any specific questions?"
                  rows={2}
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 py-3 border rounded-xl hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 py-3 bg-orange-500 text-white rounded-xl hover:bg-orange-600 disabled:opacity-50"
                >
                  {submitting ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

// Main School Match Page
export default function SchoolMatchPage() {
  const [searchQuery, setSearchQuery] = useState({
    location: 'Indore',
    budget_min: 0,
    budget_max: 100000,
    board: '',
    class_needed: 'Class 1'
  });
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState(null);

  const handleSearch = async () => {
    setLoading(true);
    setSearched(true);
    try {
      const res = await fetch(`${API_URL}/api/school-match/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(searchQuery)
      });
      const data = await res.json();
      setSchools(data.schools || []);
    } catch (err) {
      console.error('Search error:', err);
    }
    setLoading(false);
  };

  const handleInterest = async (interestData) => {
    try {
      const res = await fetch(`${API_URL}/api/school-match/express-interest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(interestData)
      });
      return await res.json();
    } catch (err) {
      console.error('Interest error:', err);
      return { success: false };
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white py-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl md:text-4xl font-bold mb-4">
            🎓 Find the Perfect School
          </h1>
          <p className="text-lg opacity-90 mb-8">
            अपने बच्चे के लिए सबसे अच्छा स्कूल खोजें
          </p>

          {/* Search Form */}
          <div className="bg-white rounded-2xl p-6 shadow-xl text-left">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">📍 Location</label>
                <input
                  type="text"
                  value={searchQuery.location}
                  onChange={(e) => setSearchQuery({...searchQuery, location: e.target.value})}
                  className="w-full px-4 py-3 border rounded-xl text-gray-800"
                  placeholder="City or Area"
                />
              </div>

              {/* Class */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">📚 Class</label>
                <select
                  value={searchQuery.class_needed}
                  onChange={(e) => setSearchQuery({...searchQuery, class_needed: e.target.value})}
                  className="w-full px-4 py-3 border rounded-xl text-gray-800"
                >
                  {['Nursery', 'LKG', 'UKG', ...Array.from({length: 12}, (_, i) => `Class ${i + 1}`)].map(cls => (
                    <option key={cls} value={cls}>{cls}</option>
                  ))}
                </select>
              </div>

              {/* Budget */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">💰 Max Budget</label>
                <select
                  value={searchQuery.budget_max}
                  onChange={(e) => setSearchQuery({...searchQuery, budget_max: Number(e.target.value)})}
                  className="w-full px-4 py-3 border rounded-xl text-gray-800"
                >
                  <option value={30000}>₹30,000/year</option>
                  <option value={50000}>₹50,000/year</option>
                  <option value={75000}>₹75,000/year</option>
                  <option value={100000}>₹1,00,000/year</option>
                  <option value={200000}>₹2,00,000+/year</option>
                </select>
              </div>

              {/* Board */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">🏫 Board</label>
                <select
                  value={searchQuery.board}
                  onChange={(e) => setSearchQuery({...searchQuery, board: e.target.value})}
                  className="w-full px-4 py-3 border rounded-xl text-gray-800"
                >
                  <option value="">Any Board</option>
                  <option value="CBSE">CBSE</option>
                  <option value="ICSE">ICSE</option>
                  <option value="State">State Board</option>
                  <option value="IB">IB</option>
                </select>
              </div>
            </div>

            <button
              onClick={handleSearch}
              disabled={loading}
              className="w-full mt-6 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl font-bold text-lg hover:shadow-lg transition-all disabled:opacity-50"
            >
              {loading ? '🔍 Searching...' : '🔍 Search Schools'}
            </button>
          </div>
        </div>
      </div>

      {/* Results Section */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {loading && (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
          </div>
        )}

        {!loading && searched && schools.length === 0 && (
          <div className="text-center py-12">
            <span className="text-6xl">🏫</span>
            <h3 className="text-xl font-semibold mt-4">No Schools Found</h3>
            <p className="text-gray-500 mt-2">Try adjusting your search criteria</p>
          </div>
        )}

        {!loading && schools.length > 0 && (
          <>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">
                🎯 {schools.length} Schools Found in {searchQuery.location}
              </h2>
              <div className="text-sm text-gray-500">
                Sorted by Match Score
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {schools.map((school, idx) => (
                <SchoolCard 
                  key={idx} 
                  school={school} 
                  onInterest={() => setSelectedSchool(school)}
                />
              ))}
            </div>
          </>
        )}

        {!searched && (
          <div className="text-center py-12">
            <span className="text-6xl">👆</span>
            <h3 className="text-xl font-semibold mt-4">Start Your Search</h3>
            <p className="text-gray-500 mt-2">Enter your location and requirements above</p>
          </div>
        )}
      </div>

      {/* Interest Modal */}
      <InterestModal
        isOpen={!!selectedSchool}
        school={selectedSchool}
        onClose={() => setSelectedSchool(null)}
        onSubmit={handleInterest}
      />

      {/* Footer */}
      <div className="bg-gray-100 py-6 text-center text-gray-500 text-sm">
        <p>Powered by शिक्षा मित्र | Find the best school for your child</p>
      </div>
    </div>
  );
}
